/**
 * Generated vehicle imagery.
 *
 * Stored in `generated_image_url`, never in `photo_url`. That separation is the
 * point: a generated picture is an illustration of a car like this one, not a
 * photograph of this one, and the two must never be silently interchangeable on
 * a listing. The UI badges generated images "AI" wherever they appear.
 *
 * Never trusts `res.ok` alone — the parsed body has to carry an image — which is
 * the same rule the PPSR and SMS work established after a provider returned 200
 * with an error payload.
 */

import { supabase } from '../lib/supabase'
import { Deal } from '../types'

export interface ImageResult {
  ok: boolean
  message: string
  imageUrl?: string
}

const vehicleBits = (d: Deal) => ({
  year: d.autograb_year ?? d.vehicle_year ?? undefined,
  make: d.autograb_make || d.vehicle_make || undefined,
  model: d.autograb_model || d.vehicle_model || undefined,
  colour: d.vehicle_color || d.autograb_color || undefined,
})

export async function generateVehicleImage(deal: Deal, userId: string): Promise<ImageResult> {
  let res: Response
  try {
    res = await fetch('/api/generate-vehicle-image', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vehicle: vehicleBits(deal) }),
    })
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'Could not reach the image service.' }
  }

  const contentType = res.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    return { ok: false, message: 'Image generation is not deployed on this environment.' }
  }

  const body = await res.json().catch(() => ({}) as any)

  if (!res.ok || !body?.success || !body?.imageUrl) {
    return {
      ok: false,
      message: body?.error || `Image generation failed (${res.status}).`,
    }
  }

  const { error } = await supabase
    .from('deals')
    .update({
      generated_image_url: body.imageUrl,
      generated_image_at: new Date().toISOString(),
      generated_image_by: userId,
      generated_image_prompt: body.prompt || null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', deal.id)

  if (error) {
    return { ok: false, message: `The image was generated but could not be saved: ${error.message}` }
  }

  return { ok: true, message: 'Image generated and saved against the deal.', imageUrl: body.imageUrl }
}

export async function clearVehicleImage(dealId: string): Promise<void> {
  const { error } = await supabase
    .from('deals')
    .update({
      generated_image_url: null,
      generated_image_at: null,
      generated_image_prompt: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', dealId)
  if (error) throw error
}
